import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, Copy, Download, FlaskConical, Play } from 'lucide-react';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import {
  axisFor,
  ibraApi,
  EvalFailure,
  EvalRunDetail,
  EvalRunSummary,
  EvalSuite,
  IbraLane,
  Severity,
  SUITES,
} from '../../api/ibra';
import { Card } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { PageHeader } from '../../components/ui/PageHeader';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { copyText, downloadText, runFilename, runToMarkdown } from './export';

/** A run takes the better part of a minute, so the list refreshes itself
 *  while one is in flight rather than making somebody hit reload. */
const POLL_MS = 5000;

const suiteSelectStyle: React.CSSProperties = {
  height: 38,
  padding: '0 32px 0 12px',
  fontSize: 13,
  fontFamily: 'var(--font-family)',
  color: 'var(--neutral-700)',
  background: 'var(--neutral-100)',
  border: '1px solid var(--neutral-300)',
  borderRadius: 'var(--radius-md)',
  outline: 'none',
  cursor: 'pointer',
};

const severityVariant: Record<Severity, 'danger' | 'warning' | 'neutral'> = {
  critical: 'danger',
  major: 'warning',
  minor: 'neutral',
};

const statusVariant: Record<
  EvalRunSummary['status'],
  'success' | 'danger' | 'info'
> = {
  completed: 'success',
  failed: 'danger',
  running: 'info',
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString('en-NG', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const pct = (n: number) => `${(n * 100).toFixed(1)}%`;

/**
 * Scoring the lane classifier, and comparing models over time.
 *
 * The headline number on this page is deliberately not accuracy. A run at 92%
 * with three criticals is worse than one at 85% with none, because a critical
 * means a question about somebody's business was answered from general
 * knowledge — the model had none of their figures and answered anyway. That is
 * the one failure that produces a confident wrong answer rather than a wasted
 * round trip, so it leads.
 */
export const IbraEvals: React.FC = () => {
  usePage('Ibra Evaluation');
  const toast = useToast();

  const [runs, setRuns] = useState<EvalRunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [model, setModel] = useState('');
  // Which corpus to run and to show. One selector drives both, because a
  // score is only readable next to the other scores on the same corpus —
  // a lanes run and an actions run share a percentage and nothing else.
  const [suite, setSuite] = useState<EvalSuite>('lanes');
  const [detail, setDetail] = useState<EvalRunDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchRuns = useCallback(async () => {
    try {
      setRuns(await ibraApi.listEvals(25, suite));
    } catch (err) {
      toast.error(err, 'Could not load evaluation runs.');
    } finally {
      setLoading(false);
    }
  }, [toast, suite]);

  useEffect(() => {
    void fetchRuns();
  }, [fetchRuns]);

  // Poll only while something is actually running. A dashboard that polls
  // forever is a dashboard somebody leaves open and forgets about.
  const hasRunning = runs.some((r) => r.status === 'running');
  useEffect(() => {
    if (!hasRunning) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    pollRef.current = setInterval(() => void fetchRuns(), POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [hasRunning, fetchRuns]);

  const start = async () => {
    try {
      setStarting(true);
      const run = await ibraApi.startEval(suite, model.trim() || undefined);
      toast.success(`Evaluating ${run.model}. This takes about a minute.`);
      setModel('');
      await fetchRuns();
    } catch (err) {
      // The backend refuses a second concurrent run and says so — surfacing
      // its message beats a generic failure, because "one is already running"
      // is not an error the user needs to fix.
      toast.error(err, 'Could not start the evaluation.');
    } finally {
      setStarting(false);
    }
  };

  const suiteDescription =
    suite === 'actions'
      ? 'Scores what Ibra offers to do against a fixed corpus — proposing an invoice, asking for a missing figure, or declining. Proposing something nobody asked for is the failure that leads.'
      : 'Scores the lane classifier against a fixed corpus. Run it against a different model to find out whether a cheaper or self-hosted one holds up.';

  const openDetail = async (run: EvalRunSummary) => {
    if (run.status === 'running') return;
    try {
      setDetailLoading(true);
      setDetail(await ibraApi.getEval(run.id));
    } catch (err) {
      toast.error(err, 'Could not load that run.');
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = [
    {
      key: 'model',
      header: 'Model',
      render: (r: EvalRunSummary) => (
        <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>
          {r.model}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r: EvalRunSummary) => (
        <Badge variant={statusVariant[r.status]}>{r.status}</Badge>
      ),
    },
    {
      key: 'critical',
      header: 'Critical',
      render: (r: EvalRunSummary) =>
        r.status !== 'completed' ? (
          <span style={{ color: 'var(--neutral-500)' }}>—</span>
        ) : r.critical > 0 ? (
          <Badge variant="danger">{r.critical}</Badge>
        ) : (
          <Badge variant="success">0</Badge>
        ),
    },
    {
      key: 'score',
      header: 'Correct',
      render: (r: EvalRunSummary) =>
        r.status === 'completed' ? (
          <span>
            {r.correct}/{r.total}{' '}
            <span style={{ color: 'var(--neutral-500)' }}>
              ({pct(r.accuracy)})
            </span>
          </span>
        ) : (
          <span style={{ color: 'var(--neutral-500)' }}>—</span>
        ),
    },
    {
      key: 'other',
      header: 'Major / minor',
      render: (r: EvalRunSummary) =>
        r.status === 'completed' ? (
          <span style={{ color: 'var(--neutral-600)' }}>
            {r.major} / {r.minor}
          </span>
        ) : (
          <span style={{ color: 'var(--neutral-500)' }}>—</span>
        ),
    },
    {
      key: 'when',
      header: 'Started',
      render: (r: EvalRunSummary) => (
        <span style={{ color: 'var(--neutral-600)', fontSize: 13 }}>
          {formatDate(r.startedAt)}
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Ibra evaluation"
        description={suiteDescription}
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              value={suite}
              onChange={(e) => {
                setSuite(e.target.value as EvalSuite);
                setLoading(true);
              }}
              style={suiteSelectStyle}
            >
              {SUITES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <Input
              placeholder="Model (blank = configured default)"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              style={{ width: 280 }}
            />
            <Button
              onClick={() => void start()}
              loading={starting}
              disabled={hasRunning}
            >
              <Play size={14} style={{ marginRight: 6 }} />
              {hasRunning ? 'Running…' : 'Run evaluation'}
            </Button>
          </div>
        }
      />

      <Card>
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            padding: '4px 2px 14px',
            color: 'var(--neutral-600)',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>
            A <strong>critical</strong> is a question about a business answered
            from general knowledge — the model had none of their figures and
            answered anyway. That matters more than the percentage: a run at 92%
            with three criticals is worse than one at 85% with none.
          </span>
        </div>

        {loading ? (
          <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
            <Spinner />
          </div>
        ) : runs.length === 0 ? (
          <EmptyState
            title="No evaluations yet"
            description="Run one to get a baseline for the model currently answering."
            icon={FlaskConical}
          />
        ) : (
          <Table
            columns={columns}
            data={runs}
            onRowClick={(r) => void openDetail(r)}
            emptyMessage="No evaluation runs"
          />
        )}
      </Card>

      <Modal
        isOpen={detail !== null || detailLoading}
        onClose={() => setDetail(null)}
        title={detail ? `${detail.model} — ${detail.correct}/${detail.total}` : 'Loading…'}
        maxWidth={860}
      >
        {detailLoading || !detail ? (
          <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
            <Spinner />
          </div>
        ) : (
          <>
            <ExportBar detail={detail} toast={toast} />
            <RunDetail detail={detail} />
          </>
        )}
      </Modal>
    </div>
  );
};

/**
 * Getting a run out of here and into a message to somebody.
 *
 * Markdown rather than a screenshot: this report is numbers, and a reader who
 * has to squint counts off an image is a reader who can misread them. Text
 * survives the trip exactly, and pastes into anything.
 */
const ExportBar: React.FC<{
  detail: EvalRunDetail;
  toast: ReturnType<typeof useToast>;
}> = ({ detail, toast }) => {
  const [copied, setCopied] = useState(false);

  // The tick is a claim that the clipboard now holds the report, so it should
  // only appear if the write actually resolved.
  const copy = async () => {
    try {
      await copyText(runToMarkdown(detail));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(null, 'Could not reach the clipboard. Try Download instead.');
    }
  };

  return (
    // A bordered strip on a tinted fill, because the modal body is pure white
    // and so is a `secondary` button — a bare button row here reads as two
    // hairline outlines and gets missed entirely.
    <div
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        padding: '12px 16px',
        marginBottom: 24,
        background: 'var(--neutral-200)',
        border: '1px solid var(--neutral-300)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      <span style={{ fontSize: 13, color: 'var(--neutral-600)' }}>
        Export this run as Markdown — tables, misroutes and all.
      </span>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="primary" onClick={() => void copy()}>
          {copied ? <Check size={15} /> : <Copy size={15} />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button
          variant="secondary"
          onClick={() => downloadText(runFilename(detail), runToMarkdown(detail))}
        >
          <Download size={15} />
          Download
        </Button>
      </div>
    </div>
  );
};

const RunDetail: React.FC<{ detail: EvalRunDetail }> = ({ detail }) => {
  if (detail.status === 'failed') {
    return (
      <div style={{ color: 'var(--danger-900)', fontSize: 14 }}>
        {detail.error ?? 'The run failed without recording a reason.'}
      </div>
    );
  }

  const failures = detail.failures ?? [];
  const order: Severity[] = ['critical', 'major', 'minor'];
  // The run says which corpus it scored, so an old row keeps reading
  // correctly after a new suite is added rather than being relabelled.
  const isActions = detail.suite === 'actions';
  const axis = axisFor(detail.suite);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        <Figure label="Critical" value={detail.critical} tone={detail.critical ? 'danger' : 'good'} />
        <Figure label="Major" value={detail.major} tone={detail.major ? 'warn' : 'good'} />
        <Figure label="Minor" value={detail.minor} tone="plain" />
        <Figure label="Accuracy" value={pct(detail.accuracy)} tone="plain" />
      </div>

      {detail.byLane ? (
        <section>
          <SectionTitle>{isActions ? 'Recall by outcome' : 'Recall by lane'}</SectionTitle>
          <p style={subtle}>
            {isActions
              ? 'Of the messages that should have got each outcome, how many did. A model can score well overall and still never ask a question.'
              : 'Of the questions that should be each lane, how many were. A model can score well overall while being useless on one lane.'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {axis.keys.map((lane) => {
              const l = detail.byLane?.[lane];
              if (!l) return null;
              return (
                <div key={lane} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ width: 130, fontSize: 13 }}>{axis.label(lane)}</span>
                  <div
                    style={{
                      flex: 1,
                      height: 8,
                      borderRadius: 4,
                      background: 'var(--neutral-200)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${l.recall * 100}%`,
                        height: '100%',
                        background:
                          l.recall >= 0.9
                            ? 'var(--green-500, #16a34a)'
                            : l.recall >= 0.7
                              ? 'var(--warning-500, #d97706)'
                              : 'var(--danger-500, #dc2626)',
                      }}
                    />
                  </div>
                  <span style={{ width: 88, fontSize: 13, textAlign: 'right', color: 'var(--neutral-600)' }}>
                    {l.correct}/{l.expected} · {pct(l.recall)}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {detail.confusion ? (
        <section>
          <SectionTitle>Where it went instead</SectionTitle>
          <p style={subtle}>
            {isActions
              ? 'Rows are what the message should have got; columns are what it got.'
              : 'Rows are what the question should have been; columns are where it went.'}
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 13, minWidth: 520 }}>
              <thead>
                <tr>
                  <th style={{ ...cell, textAlign: 'left', color: 'var(--neutral-500)' }} />
                  {axis.keys.map((l) => (
                    <th key={l} style={{ ...cell, color: 'var(--neutral-500)', fontWeight: 500 }}>
                      {axis.label(l)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {axis.keys.map((expected) => (
                  <tr key={expected}>
                    <td style={{ ...cell, textAlign: 'left', color: 'var(--neutral-600)' }}>
                      {axis.label(expected)}
                    </td>
                    {axis.keys.map((actual) => {
                      const n = detail.confusion?.[expected]?.[actual] ?? 0;
                      const isDiagonal = expected === actual;
                      return (
                        <td
                          key={actual}
                          style={{
                            ...cell,
                            fontWeight: isDiagonal ? 600 : 400,
                            color: n === 0
                              ? 'var(--neutral-400)'
                              : isDiagonal
                                ? 'var(--green-900)'
                                : 'var(--danger-900)',
                          }}
                        >
                          {n}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {failures.length > 0 ? (
        <section>
          <SectionTitle>{isActions ? 'Wrong calls' : 'Misroutes'}</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {order.flatMap((sev) =>
              failures
                .filter((f) => f.severity === sev)
                .map((f, i) => (
                  <FailureRow key={`${sev}-${i}`} failure={f} label={axis.label} />
                )),
            )}
          </div>
        </section>
      ) : (
        <p style={subtle}>
          {isActions
            ? 'Nothing wrong. Every message got the outcome it should have.'
            : 'No misroutes. Every question landed in the right lane.'}
        </p>
      )}
    </div>
  );
};

const FailureRow: React.FC<{
  failure: EvalFailure;
  label: (key: string) => string;
}> = ({ failure, label }) => (
  <div
    style={{
      border: '1px solid var(--neutral-200)',
      borderRadius: 8,
      padding: 12,
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    }}
  >
    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
      <Badge variant={severityVariant[failure.severity]}>{failure.severity}</Badge>
      <span style={{ fontSize: 14 }}>{failure.question}</span>
    </div>
    <div style={{ fontSize: 13, color: 'var(--neutral-600)' }}>
      Expected <strong>{label(failure.expected)}</strong>, got{' '}
      <strong>{label(failure.actual)}</strong>
      {failure.reason ? ` — ${failure.reason}` : ''}
    </div>
    {failure.note ? (
      // Corpus notes exist because some of these cases are genuinely arguable.
      // Showing them here stops somebody "fixing" a deliberate boundary case.
      <div style={{ fontSize: 12, color: 'var(--neutral-500)', fontStyle: 'italic' }}>
        {failure.note}
      </div>
    ) : null}
  </div>
);

const Figure: React.FC<{
  label: string;
  value: string | number;
  tone: 'good' | 'warn' | 'danger' | 'plain';
}> = ({ label, value, tone }) => {
  const color =
    tone === 'danger'
      ? 'var(--danger-900)'
      : tone === 'warn'
        ? 'var(--warning-900)'
        : tone === 'good'
          ? 'var(--green-900)'
          : 'var(--neutral-800)';
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color }}>{value}</div>
    </div>
  );
};

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 4px' }}>{children}</h3>
);

const subtle: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--neutral-600)',
  margin: '0 0 12px',
  lineHeight: 1.5,
};

const cell: React.CSSProperties = {
  padding: '6px 12px',
  textAlign: 'center',
  border: '1px solid var(--neutral-200)',
};

export type { IbraLane };
